import React, { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";

function InfoView() {
    const [karren, setKarren] = useState([]);
    const { t, language } = useTranslation();

    const fetchStatusinfo = useCallback(() => {
        const url = process.env.REACT_APP_ICECORP_API_HREF+"v1/icecreamvanmarkerdata?company_id="+process.env.REACT_APP_COMPANY_ID;
        fetch(url)
        .then(response => response.json())
        .then(data => {
            setKarren(data.data || []);
        })
        .catch(() => {
            // Silent fail, will retry
        });
    }, []);

    useEffect(() => {
        fetchStatusinfo();
        const interval = setInterval(fetchStatusinfo, 5000);
        return () => clearInterval(interval);
    }, [fetchStatusinfo]);

    const statusToText = (kar) => {
        const statusTexts = t('infoPage.truckStatuses', {});
        const name = kar.status_payload?.scheduled_at_name;

        switch (kar.status) {
            case "STARTUP":
                return <>{statusTexts.startup || 'Vertrekt zodadelijk'}{name ? <> {statusTexts.towards || 'richting'} {name}</> : null}</>;
            case "EN_ROUTE_TO_ZONE":
                return <>{statusTexts.enRouteToZone || 'Onderweg'}{name ? <> {statusTexts.to || 'naar'} {name}</> : <> {statusTexts.toZone || 'naar zone'}</>}</>;
            case "IN_ZONE":
                return <>{statusTexts.inZone || 'Aan het rijden'}{name ? <> {statusTexts.in || 'in'} {name}</> : null}</>;
            case "EN_ROUTE_TO_DEPOT":
            case "CLEANUP":
            case "FINISHED":
                return <>{statusTexts.soldOut || 'Uitverkocht'}</>;
            case "SCHEDULED":
                return <>{statusTexts.scheduled || 'Gepland'}{name ? <> {statusTexts.in || 'in'} {name}</> : null}</>;
            case "NOT_SCHEDULED":
                return <>{statusTexts.notScheduled || 'Niet gepland vandaag'}</>;
            case "EN_ROUTE_TO_ICECREAM_VAN_RESERVATION":
                return <>{statusTexts.enRouteToReservation || 'Onderweg naar reservatie'}</>;
            case "AT_ICECREAM_VAN_RESERVATION":
                return <>{statusTexts.atReservation || 'Op reservatie'}</>;
            case "ON_BREAK":
                return <>{statusTexts.onBreak || 'In pauze'}</>;
            default:
                return <></>;
        }
    };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const headerOffset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
    };

    const $versionInfo = window.versionInfo;

    // Get translated content
    const pageTitle = t('infoPage.title', 'Info');
    const nav = t('infoPage.navigation', {});
    const sections = t('infoPage.sections', {});
    const about = t('infoPage.about', {});
    const appFeatures = t('infoPage.appFeatures', []);
    const faq = t('infoPage.faq', {});
    const contact = t('infoPage.contact', {});
    const footer = t('infoPage.footer', {});
    const reservationUrl = t('infoPage.reservationUrl', 'https://reservatie.foubert.eu');
    const reserveButton = t('infoPage.reserveButton', 'Reserveer een ijskar!');

    // Status labels
    const statusLabels = t('infoPage.statusLabels', {});

    // Status table content
    const statusContent = karren.length > 0 ? (
        <div className="content-card" style={{padding: 0, overflow: 'hidden'}}>
            <table className="status-table">
                <tbody>
                    {karren.map((kar, index) => (
                        <tr key={index}>
                            <td>
                                <div className="status-name">
                                    <span className={`status-indicator ${kar.available ? 'available' : 'unavailable'}`}></span>
                                    {kar.title}
                                </div>
                            </td>
                            <td>
                                <span className={`status-badge ${kar.available ? 'available' : 'unavailable'}`}>
                                    {kar.available
                                        ? (statusLabels.available || "Beschikbaar")
                                        : (statusLabels.unavailable || "Niet beschikbaar")}
                                </span>
                            </td>
                            <td className="status-text">
                                {statusToText(kar)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    ) : (
        <div className="content-card">
            <div className="loading-placeholder">
                <div className="loading-spinner"></div>
                <p>{statusLabels.loading || 'De statussen worden opgehaald...'}</p>
            </div>
        </div>
    );

    // FAQ categories
    const faqCategories = faq.categories || [];

    return (
        <div className="content container page-container">
            <div className="page-hero" style={{backgroundImage: "url('/images/about/header.png')"}}>
                <div className="page-hero-overlay"></div>
            </div>

            <h1 className="page-title">{pageTitle}</h1>

            {/* Quick Navigation */}
            <div className="quick-nav">
                <button type="button" className="quick-nav-item" onClick={() => scrollToSection('reserveren')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    {nav.reserve || 'Reserveren'}
                </button>
                <button type="button" className="quick-nav-item" onClick={() => scrollToSection('status')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    {nav.liveStatus || 'Live Status'}
                </button>
                <button type="button" className="quick-nav-item" onClick={() => scrollToSection('over-foubert')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                        <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                    {nav.aboutCompany || 'Over Foubert'}
                </button>
                <button type="button" className="quick-nav-item" onClick={() => scrollToSection('over-app')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                        <line x1="12" y1="18" x2="12.01" y2="18"/>
                    </svg>
                    {nav.aboutApp || 'Over de App'}
                </button>
                <button type="button" className="quick-nav-item" onClick={() => scrollToSection('faq')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    {nav.faq || 'FAQ'}
                </button>
            </div>

            {/* CTA - Reserveer een ijskar */}
            <div id="reserveren" className="cta-section">
                <a href={reservationUrl} target="_blank" rel="noreferrer" className="btn">
                    {reserveButton}
                </a>
            </div>

            {/* Live Status Section */}
            <h2 id="status" className="section-title" style={{marginTop: '2rem', marginBottom: '1rem'}}>
                <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                </svg>
                {sections.liveStatus || 'Live Status'}
            </h2>
            {statusContent}

            {/* Over Foubert Section */}
            <h2 id="over-foubert" className="section-title" style={{marginTop: '2.5rem', marginBottom: '1rem'}}>
                <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                {sections.aboutCompany || 'Over Foubert'}
            </h2>

            <div className="info-grid">
                <div className="content-card highlight">
                    <h3 className="section-title">
                        <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {about.historyTitle || 'Historiek'}
                    </h3>
                    <p style={{color: 'var(--text-secondary)', lineHeight: '1.7'}}>
                        {about.historyText || 'In 2012 begonnen we met onze eerste ijskar, in 2013 kwamen er 2 nieuwe bij! In 2014 begonnen we met ons "huis van de ijskarren" zodat we er in 2015 een reeks bij kunnen zetten! Ondertussen rijden we met 16 ijskarretjes door het ruime Waasland!'}
                    </p>
                </div>

                <div className="content-card">
                    <h3 className="section-title">
                        <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                            <circle cx="12" cy="10" r="3"/>
                        </svg>
                        {about.whereWhenTitle || 'Waar & Wanneer'}
                    </h3>
                    <p style={{color: 'var(--text-secondary)', lineHeight: '1.7'}}>
                        {about.whereText || 'Regio Sint-Niklaas, Temse, Hamme, Dendermonde, Sint-Gillis-Waas, Lokeren, Zwijndrecht, Beveren, Moerbeke, Eksaarde, Daknam.'}
                    </p>
                    <p style={{color: 'var(--text-secondary)', lineHeight: '1.7', marginTop: '0.75rem'}}>
                        {about.whenText || 'We rijden rond vanaf de eerste lentezon tot midden oktober, afhankelijk van het weer.'}
                    </p>
                </div>
            </div>

            {/* Over de App Section */}
            <h2 id="over-app" className="section-title" style={{marginTop: '2.5rem', marginBottom: '1rem'}}>
                <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                    <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
                {sections.aboutApp || 'Over de App'}
            </h2>

            <div className="app-info-card">
                {(appFeatures.length > 0 ? appFeatures : [
                    { title: 'Realtime beschikbaarheid', description: 'IJskarren die niet beschikbaar zijn zijn duidelijker zichtbaar op de kaart.', icon: 'clock' },
                    { title: 'Slimme buurtdetectie', description: 'De app checkt vooraf of er beschikbare ijskarren in jouw buurt zijn.', icon: 'location' },
                    { title: 'Progressive Web App', description: 'Snelle en soepele beleving op je telefoon dankzij PWA-technologie.', icon: 'bolt' }
                ]).map((feature, index) => (
                    <div className="app-feature" key={index}>
                        <div className="app-feature-icon">
                            {feature.icon === 'clock' && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <path d="M12 8v4l3 3"/>
                                </svg>
                            )}
                            {feature.icon === 'location' && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                    <circle cx="12" cy="10" r="3"/>
                                </svg>
                            )}
                            {feature.icon === 'bolt' && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                                </svg>
                            )}
                        </div>
                        <div className="app-feature-content">
                            <h4>{feature.title}</h4>
                            <p>{feature.description}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* FAQ Section */}
            <h2 id="faq" className="section-title" style={{marginTop: '2.5rem', marginBottom: '1rem'}}>
                <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                {sections.faq || 'Veelgestelde vragen'}
            </h2>

            {faqCategories.map((category, catIndex) => (
                <React.Fragment key={catIndex}>
                    <h3 className="faq-category">{category.title}</h3>
                    {(category.questions || []).map((item, qIndex) => (
                        <div className="faq-card" key={qIndex}>
                            <div className="question">{item.question}</div>
                            <div className="answer" dangerouslySetInnerHTML={{ __html: item.answer }} />
                        </div>
                    ))}
                </React.Fragment>
            ))}

            {/* Contact sectie */}
            <h3 className="faq-category">{contact.title || 'Contact'}</h3>

            <div className="faq-card">
                <div className="question">{contact.questionTitle || 'Hoe kan ik contact opnemen?'}</div>
                <div className="answer">
                    <p><strong>{contact.emailLabel || 'E-mail'}:</strong> <a href={`mailto:${contact.email || 'info@foubert.eu'}`} style={{color: 'var(--company-color)'}}>{contact.email || 'info@foubert.eu'}</a></p>
                    <p style={{marginTop: '0.5rem'}}><strong>{contact.phoneLabel || 'Telefoon'}:</strong> <a href={`tel:${contact.phone || '+32486800945'}`} style={{color: 'var(--company-color)'}}>{contact.phoneDisplay || '+32 486 80 09 45'}</a> ({contact.phoneNote || 'beschikbaar tijdens kantooruren'})</p>
                    <p style={{marginTop: '0.5rem'}}><strong>{contact.websiteLabel || 'Website'}:</strong> <a href={contact.websiteUrl || 'https://www.foubert.eu'} target="_blank" rel="noreferrer" style={{color: 'var(--company-color)'}}>{contact.websiteDisplay || 'www.foubert.eu'}</a></p>
                    <p style={{marginTop: '0.5rem'}}><strong>Facebook:</strong> <a href={contact.facebookUrl || 'https://www.facebook.com/foubertijs'} target="_blank" rel="noreferrer" style={{color: 'var(--company-color)'}}>{contact.facebookName || 'Foubert IJs'}</a></p>
                    {contact.instagramUrl && (
                        <p style={{marginTop: '0.5rem'}}><strong>Instagram:</strong> <a href={contact.instagramUrl} target="_blank" rel="noreferrer" style={{color: 'var(--company-color)'}}>{contact.instagramName || 'foubertijs'}</a></p>
                    )}
                    {contact.tiktokUrl && (
                        <p style={{marginTop: '0.5rem'}}><strong>TikTok:</strong> <a href={contact.tiktokUrl} target="_blank" rel="noreferrer" style={{color: 'var(--company-color)'}}>{contact.tiktokName || '@foubertijs'}</a></p>
                    )}
                </div>
            </div>

            <div className="cta-section" style={{marginTop: '1.5rem'}}>
                <a href={reservationUrl} target="_blank" rel="noreferrer" className="btn">
                    {reserveButton}
                </a>
            </div>

            {/* Footer */}
            <div className="footer-info">
                <p>
                    {footer.termsText || 'Om deze app te gebruiken moet je akkoord gaan met onze'}{' '}
                    <Link to="/privacypolicy">{footer.privacyPolicy || 'privacyverklaring'}</Link> {footer.and || 'en'}{' '}
                    <Link to="/cookiepolicy">{footer.cookiePolicy || 'cookievoorwaarden'}</Link>.
                </p>
                <p style={{marginTop: '0.75rem', opacity: 0.7}}>
                    Buildversion: {$versionInfo} — App {footer.by || 'door'} cloudfactory.be (<a href="mailto:info@cloudfactory.be">info@cloudfactory.be</a>)
                </p>
            </div>
        </div>
    );
}

export default InfoView;
